// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Group, Center, Container } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, PasswordInput, Checkbox } from '@/components/ui/inputs';
import { IconMail, IconLock, IconArrowLeft, IconSparkles } from '@tabler/icons-react';
import { FadeIn, SlideIn, ScaleIn, HoverLift, useReducedMotion } from '@/components/animations';
import { AnimatedFormWrapper, SocialLoginButtons } from '../components';

export default function LoginAnimated() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Login attempt:', { email, password, rememberMe });
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(135deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(135deg, ${theme.colors.gray[0]} 0%, ${theme.colors.indigo[0]} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Floating orbs */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            style={{
              position: 'absolute',
              width: 100 + i * 50,
              height: 100 + i * 50,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.indigo[isDark ? 8 : 3]}, ${theme.colors.cyan[isDark ? 8 : 3]})`,
              opacity: 0.1,
              top: `${10 + i * 15}%`,
              left: `${5 + i * 20}%`,
              animation: reduceMotion ? 'none' : `float ${5 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </Box>

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <ScaleIn delay={100} duration={500}>
            <HoverLift>
              <Card variant="elevated" p="xl" radius="lg">
                <Stack gap="lg">
                  {/* Back button */}
                  <SlideIn direction="left" delay={200}>
                    <Button
                      component={Link}
                      to="/app/demo/auth-pages/login"
                      variant="subtle"
                      size="xs"
                      leftSection={<IconArrowLeft size={14} />}
                      w="fit-content"
                    >
                      Back to variants
                    </Button>
                  </SlideIn>

                  {/* Header */}
                  <FadeIn delay={300}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconSparkles size={12} />}
                        variant="gradient"
                        gradient={{ from: 'indigo', to: 'violet' }}
                        size="lg"
                      >
                        Animated Experience
                      </Badge>
                      <Title order={2} ta="center" mt="md">
                        Welcome back
                      </Title>
                      <Text c="dimmed" size="sm" ta="center">
                        Sign in with smooth animations
                      </Text>
                    </Stack>
                  </FadeIn>

                  {/* Form with staggered animations */}
                  <form onSubmit={handleSubmit}>
                    <AnimatedFormWrapper animation="stagger" delay={400} staggerDelay={100}>
                      <TextInput
                        label="Email"
                        placeholder="your@email.com"
                        leftSection={<IconMail size={16} />}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        size="md"
                        radius="md"
                      />

                      <PasswordInput
                        label="Password"
                        placeholder="Your password"
                        leftSection={<IconLock size={16} />}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                      />

                      <Group justify="space-between">
                        <Checkbox
                          label="Remember me"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.currentTarget.checked)}
                        />
                        <Anchor
                          component={Link}
                          to="/app/demo/auth-pages/forgot-password/animated"
                          size="sm"
                        >
                          Forgot password?
                        </Anchor>
                      </Group>

                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        variant="gradient"
                        gradient={{ from: 'indigo', to: 'violet' }}
                      >
                        Sign in
                      </Button>
                    </AnimatedFormWrapper>
                  </form>

                  {/* Social Login */}
                  <SlideIn direction="up" delay={800}>
                    <SocialLoginButtons
                      providers={['google', 'github']}
                      layout="horizontal"
                      variant="outline"
                    />
                  </SlideIn>

                  {/* Register Link */}
                  <FadeIn delay={900}>
                    <Text ta="center" size="sm">
                      Don't have an account?{' '}
                      <Anchor component={Link} to="/app/demo/auth-pages/register/animated" fw={500}>
                        Sign up
                      </Anchor>
                    </Text>
                  </FadeIn>
                </Stack>
              </Card>
            </HoverLift>
          </ScaleIn>
        </Container>
      </Center>

      {/* Keyframe for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </Box>
  );
}
